import { Copy, Hash } from 'lucide-react'
import type { ExtensionEntityMenuDeclaration, ExtensionMenuItem } from '@/types/integration'
import type { BasecampAssignment, BasecampProject } from '@/types/basecamp'
import { BasecampIcon } from '@/components/icons/basecamp-icon'
import { openBasecampTodoInFlowNotes } from '@/lib/basecamp-todo-integration'
import {
  BASECAMP_OPEN_IN_BASECAMP_LABEL,
  BASECAMP_VIEW_PROJECT_LABEL,
  basecampViewTodoLabel,
} from '@/lib/basecamp-todo-open-labels'
import { OpenDestinationIcons, resolveOpenExternalIcon } from '@/lib/open-destination-icons'

function openBasecampProjectInFlowNotes(
  project: { id: string; name: string; organizationId?: string }
): void {
  window.dispatchEvent(
    new CustomEvent('integration-view-change', {
      detail: {
        tabId: `basecamp-project-${project.id}`,
        title: project.name,
        type: 'integration-basecamp' as const,
        integrationData: {
          integrationId: 'basecamp' as const,
          projectId: project.id,
          ...(project.organizationId ? { organizationId: project.organizationId } : {}),
        },
      },
    })
  )
}

function todoItems(entity: unknown): ExtensionMenuItem[] {
  const todo = entity as BasecampAssignment
  const appUrl = todo._app_url ?? todo.url
  return [
    {
      id: 'open-flownotes',
      label: basecampViewTodoLabel('todo'),
      icon: OpenDestinationIcons.open,
      action: (e) => {
        const t = e as BasecampAssignment
        const projectId = t.project?.id || (t as { project_id?: string }).project_id
        if (projectId) {
          openBasecampTodoInFlowNotes(t.id, String(projectId), 'todo')
        }
      },
    },
    ...(appUrl
      ? [
          {
            id: 'open-basecamp',
            label: BASECAMP_OPEN_IN_BASECAMP_LABEL,
            icon: resolveOpenExternalIcon(BasecampIcon),
            action: () => {
              window.open(appUrl, '_blank', 'noopener,noreferrer')
            },
          } satisfies ExtensionMenuItem,
        ]
      : []),
    {
      id: 'copy-url',
      label: 'Copy Basecamp URL',
      icon: Copy,
      separatorBefore: true,
      disabled: !appUrl,
      action: () => {
        if (appUrl) void navigator.clipboard?.writeText(appUrl)
      },
    },
    {
      id: 'copy-id',
      label: 'Copy todo ID',
      icon: Hash,
      action: (e) => {
        void navigator.clipboard?.writeText((e as BasecampAssignment).id)
      },
    },
  ]
}

function projectItems(entity: unknown): ExtensionMenuItem[] {
  const project = entity as BasecampProject & { organizationId?: string }
  const appUrl = project.app_url || project.url
  return [
    {
      id: 'open-flownotes-project',
      label: BASECAMP_VIEW_PROJECT_LABEL,
      icon: OpenDestinationIcons.open,
      action: (e) => {
        const p = e as BasecampProject & { organizationId?: string }
        openBasecampProjectInFlowNotes({
          id: p.id,
          name: p.name,
          organizationId: p.organizationId,
        })
      },
    },
    ...(appUrl
      ? [
          {
            id: 'open-basecamp-project',
            label: BASECAMP_OPEN_IN_BASECAMP_LABEL,
            icon: resolveOpenExternalIcon(BasecampIcon),
            action: () => window.open(appUrl, '_blank', 'noopener,noreferrer'),
          } satisfies ExtensionMenuItem,
        ]
      : []),
    {
      id: 'copy-project-url',
      label: 'Copy Basecamp URL',
      icon: Copy,
      separatorBefore: true,
      disabled: !appUrl,
      action: () => {
        if (appUrl) void navigator.clipboard?.writeText(appUrl)
      },
    },
    {
      id: 'copy-project-id',
      label: 'Copy project ID',
      icon: Hash,
      action: (e) => {
        void navigator.clipboard?.writeText((e as BasecampProject).id)
      },
    },
  ]
}

export const basecampEntityMenus: ExtensionEntityMenuDeclaration[] = [
  { entityType: 'todo', items: (entity) => todoItems(entity) },
  { entityType: 'project', items: (entity) => projectItems(entity) },
]
